import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TGeneralTicketingNotification } from '../types/notification.type';

export type TGetGeneralTicketingNotificationResponse = ApiResponse<TGeneralTicketingNotification>;

export const getGeneralTicketingNotification = async (): Promise<TGetGeneralTicketingNotificationResponse> => {
  const url = `/notifications/general-ticketing-configuration`;

  return axiosInstance.get(url);
};

export const GET_GENERAL_TICKETING_NOTIFICATION_QUERY_KEY = 'get-general-ticketing-notification';

export const getGeneralTicketingNotificationQueryOptions = <TData = TGetGeneralTicketingNotificationResponse>(
  select?: (data: TGetGeneralTicketingNotificationResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_GENERAL_TICKETING_NOTIFICATION_QUERY_KEY],
    queryFn: () => getGeneralTicketingNotification(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGeneralTicketingNotificationQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getGeneralTicketingNotificationQueryOptions<TData>>;
  select: (data: TGetGeneralTicketingNotificationResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGeneralTicketingNotificationQuery(options: {
  queryConfig?: QueryConfig<
    typeof getGeneralTicketingNotificationQueryOptions<TGetGeneralTicketingNotificationResponse>
  >;
}): ReturnType<typeof useQuery<TGetGeneralTicketingNotificationResponse>>;

// Implementation
export function useGeneralTicketingNotificationQuery<TData = TGetGeneralTicketingNotificationResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<
    typeof getGeneralTicketingNotificationQueryOptions<TData | TGetGeneralTicketingNotificationResponse>
  >;
  select?: (data: TGetGeneralTicketingNotificationResponse) => TData;
}) {
  return useQuery({
    ...getGeneralTicketingNotificationQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetGeneralTicketingNotificationResponse ? TGetGeneralTicketingNotificationResponse : TData
    >
  >;
}
